import React, { useState, useEffect } from 'react';
import { FileCheck2, Plus, Search, Calendar, AlertTriangle, AlertOctagon, CheckCircle, Upload, Trash2, Download, Eye, X, Building2, Filter, FileText, Briefcase, Edit2, Sparkles } from 'lucide-react';
import ClearFiltersButton from './ClearFiltersButton';

import { OfficialDocument, DocType, AnalyzedFile } from '../types';
import { logActivity } from '../services/auditService';
import { apiService } from '../services/apiService';
import useCompanies from '../hooks/useCompanies';

import { useModal } from './ModalProvider';

const CertificateList: React.FC = () => {
  const [docs, setDocs] = useState<OfficialDocument[]>([]);
  const [auditMap, setAuditMap] = useState<Record<string, any[]>>({});
  const { companies, loading: companiesLoading, refresh: refreshCompanies, getCompanyName } = useCompanies();

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'VALID' | 'WARNING' | 'EXPIRED'>('ALL');

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState<OfficialDocument | null>(null);
  const [form, setForm] = useState<Partial<OfficialDocument>>({
    title: '',
    type: 'ATESTADO',
    issuer: '',
    issueDate: '',
    expirationDate: '',
    companyId: '',
    clientName: '',
    clientCNPJ: '',
    reference: '',
    workDescription: '',
    executionPeriodStart: '',
    executionPeriodEnd: '',
    workLocation: '',
    signatoryName: '',
    signatoryRole: '',
    signatoryCPF: '',
    category: '',
    hasCrea: false,
    hasArt: false,
    items: []
  });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);  // IA
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const [deleteAttachmentIds, setDeleteAttachmentIds] = useState<string[]>([]);

  // Modal de visualização de anexos
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedDocAttachments, setSelectedDocAttachments] = useState<OfficialDocument | null>(null);

  const parseLocalDate = (dateString: string): Date => {
    if (!dateString || dateString === '') return new Date('Invalid');
    const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = dateOnly.split('-').map(Number);
    if (!year || !month || !day) return new Date('Invalid');
    return new Date(year, month - 1, day);
  };

  // Helper to extract actor info from audit log
  const getActorFromLog = (log: any) => {
    if (!log) return null;
    const parsed = log.parsedDetails || (typeof log.details === 'string' ? (() => { try { return JSON.parse(log.details); } catch (e) { return null; } })() : null) || log;
    const name = parsed?.actor?.name || log.userName || log.user?.name || null;
    const email = parsed?.actor?.email || log.user?.email || null;
    const timestamp = log.timestamp || parsed?.timestamp || null;
    return { name, email, timestamp };
  };

  const calculateStatus = (expirationDate?: string) => {
    if (!expirationDate) return 'NO_EXPIRATION';
    const today = new Date(); today.setHours(0,0,0,0);
    const exp = parseLocalDate(expirationDate); exp.setHours(0,0,0,0);
    const diffTime = exp.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'EXPIRED';
    if (diffDays <= 15) return 'WARNING';
    return 'VALID';
  };

  const getDaysRemaining = (dateString?: string) => {
    if (!dateString) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const exp = parseLocalDate(dateString);
    if (isNaN(exp.getTime())) return null;
    exp.setHours(0,0,0,0);
    const diffTime = exp.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const certs = await apiService.getTechnicalCertificates();
        setDocs(certs);
      } catch (err) {
        console.error('Erro ao carregar atestados:', err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!docs || docs.length === 0) return;
    let cancelled = false;
    const fetchLogs = async () => {
      try {
        const ids = docs.map(d => d.id);
        const res = await apiService.getAuditLogsByResourceBatch('TechnicalCertificate', ids);
        const map: Record<string, any[]> = {};
        for (const d of docs) map[d.id] = res[d.id] || [];
        if (!cancelled) setAuditMap(map);
      } catch (e) {
        const map: Record<string, any[]> = {};
        for (const d of docs) map[d.id] = [];
        if (!cancelled) setAuditMap(map);
      }
    };
    fetchLogs();
    return () => { cancelled = true; };
  }, [docs]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setAiSuccess(false);
    }
  };

  const handleAiExtraction = async (file: File) => {
    setIsExtracting(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64String = result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const extractedData = await apiService.extractCertificateData(base64, file.name);

      const convertDate = (dateStr: string) => {
        if (!dateStr || dateStr === 'NÃO CONSTA') return '';
        const parts = dateStr.split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        return '';
      };

      setForm(prev => ({
        ...prev,
        title: extractedData.title && extractedData.title !== 'NÃO CONSTA' ? extractedData.title : (prev.title || file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, '').replace(/[-_]/g, ' ')),
        issuer: extractedData.issuing_agency && extractedData.issuing_agency !== 'NÃO CONSTA' ? extractedData.issuing_agency : prev.issuer,
        issueDate: convertDate(extractedData.issue_date) || prev.issueDate,
        expirationDate: convertDate(extractedData.expiration_date) || prev.expirationDate,
        type: 'ATESTADO' as DocType,
        // Novos campos específicos de atestados
        clientName: extractedData.client_name && extractedData.client_name !== 'NÃO CONSTA' ? extractedData.client_name : prev.clientName,
        clientCNPJ: extractedData.client_cnpj && extractedData.client_cnpj !== 'NÃO CONSTA' ? extractedData.client_cnpj : prev.clientCNPJ,
        reference: extractedData.reference && extractedData.reference !== 'NÃO CONSTA' ? extractedData.reference : prev.reference,
        workDescription: extractedData.work_description && extractedData.work_description !== 'NÃO CONSTA' ? extractedData.work_description : prev.workDescription,
        workLocation: extractedData.work_location && extractedData.work_location !== 'NÃO CONSTA' ? extractedData.work_location : prev.workLocation,
        executionPeriodStart: convertDate(extractedData.execution_period_start) || prev.executionPeriodStart,
        executionPeriodEnd: convertDate(extractedData.execution_period_end) || prev.executionPeriodEnd,
        signatoryName: extractedData.signatory_name && extractedData.signatory_name !== 'NÃO CONSTA' ? extractedData.signatory_name : prev.signatoryName,
        signatoryRole: extractedData.signatory_role && extractedData.signatory_role !== 'NÃO CONSTA' ? extractedData.signatory_role : prev.signatoryRole,
        signatoryCPF: extractedData.signatory_cpf && extractedData.signatory_cpf !== 'NÃO CONSTA' ? extractedData.signatory_cpf : prev.signatoryCPF,

        // Categoria sugerida pela IA (prioriza o campo category dedicado, senão usa object)
        category: extractedData.category && extractedData.category !== 'NÃO CONSTA' ? extractedData.category : (extractedData.object && extractedData.object !== 'NÃO CONSTA' ? extractedData.object : prev.category),

        // Itens quantitativos sugeridos pela IA
        items: (extractedData.items && Array.isArray(extractedData.items)) ? extractedData.items.map((it: any) => ({
            description: it.description || '',
            quantity: Number(it.quantity) || 0,
            unit: it.unit || 'Un'
        })) : prev.items
      }));
      setAiSuccess(true);
      await showAlert('Dados extraídos com sucesso! Revise e clique em Salvar.');
    } catch (error) {
      console.error('Erro extraindo IA:', error);
      setForm(prev => ({ ...prev, title: prev.title || file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, '').replace(/[-_]/g, ' ') }));
      await showAlert('Não foi possível extrair dados automaticamente. Preencha manualmente.');
    } finally {
      setIsExtracting(false);
    }
  };

  const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        handleAiExtraction(file);
      } else {
        await showAlert('Apenas arquivos PDF ou imagens (PNG/JPG) são aceitos.');
      }
    }
  };

  const handleOpenModal = (doc?: OfficialDocument) => {
    let defaultCompanyId = '';
    if (companyFilter !== 'ALL') defaultCompanyId = companyFilter;
    else if (companies.length > 0) {
      const def = companies.find(c => c.isDefault);
      defaultCompanyId = def ? def.id : companies[0].id;
    }

    if (doc) {
      setEditingDoc(doc);
      setForm({
        title: doc.title,
        type: doc.type,
        issuer: doc.issuer,
        issueDate: doc.issueDate,
        expirationDate: doc.expirationDate,
        companyId: doc.companyId || defaultCompanyId,
        clientName: doc.clientName || '',
        clientCNPJ: doc.clientCNPJ || '',
        reference: doc.reference || '',
        workDescription: doc.workDescription || '',
        executionPeriodStart: doc.executionPeriodStart || '',
        executionPeriodEnd: doc.executionPeriodEnd || '',
        workLocation: doc.workLocation || '',
        signatoryName: doc.signatoryName || '',
        signatoryRole: doc.signatoryRole || '',
        signatoryCPF: doc.signatoryCPF || '',
        category: doc.category || '',
        hasCrea: doc.hasCrea || false,
        hasArt: doc.hasArt || false,
        items: doc.items || [],
        attachments: doc.attachments || []
      });
    } else {
      setEditingDoc(null);
      setForm({
        title: '',
        type: 'ATESTADO',
        issuer: '',
        issueDate: '',
        expirationDate: '',
        companyId: defaultCompanyId,
        clientName: '',
        clientCNPJ: '',
        reference: '',
        workDescription: '',
        executionPeriodStart: '',
        executionPeriodEnd: '',
        workLocation: '',
        signatoryName: '',
        signatoryRole: '',
        signatoryCPF: '',
        category: '',
        hasCrea: false,
        hasArt: false
      });
    }
    setSelectedFile(null);
    setIsExtracting(false);
    setAiSuccess(false);
    setIsModalOpen(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCompanyFilter('ALL');
    setStatusFilter('ALL');
  };

  const handleSave = async () => {
    if (isSaving) {
      console.warn('Save already in progress, ignoring duplicate request');
      return;
    }
    if (!form.title) {
      await showAlert('Título é obrigatório.');
      return;
    }

    // Require the user to select a company explicitly to avoid creating records
    // that won't match the current company filter (causing them to 'sumir').
    if (!form.companyId) {
      await showAlert('Selecione a empresa antes de salvar.');
      return;
    }

    setIsSaving(true);
    try {
      const finalCompanyId = form.companyId || (companies.length > 0 ? companies[0].id : '');
      if (!finalCompanyId) console.warn('Saving atestado without companyId — finalCompanyId is empty string');
    let finalFileUrl = editingDoc?.fileUrl;

    try {
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const key = `documents/${finalCompanyId}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
        const { uploadUrl } = await apiService.getPresignedUploadUrl(key, selectedFile.type);
        await apiService.uploadFileToS3(uploadUrl, selectedFile);
        finalFileUrl = key;
      }

      // add a clientRequestId to help trace duplicate submissions across client/server
      const clientRequestId = (globalThis as any).crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const payload: any = {
        ...form,
        deleteAttachmentIds: deleteAttachmentIds.length > 0 ? deleteAttachmentIds : undefined,
        companyId: finalCompanyId,
        status: calculateStatus(form.expirationDate),
        fileUrl: finalFileUrl,
        type: 'ATESTADO',
        clientRequestId
      };

      // expose last payload/response for easy debugging in the browser console
      try { (window as any).__lastAtestadoDebug = { payload }; } catch (e) { /* ignore */ }

      // log payload to help debug duplicate creations
      console.debug('Atestado payload to save:', payload);

      if (editingDoc) {
        const res = await apiService.updateTechnicalCertificate(editingDoc.id, payload);
        console.debug('Atestado updated response:', res);
        try { (window as any).__lastAtestadoDebug.response = res; } catch (e) {}
        logActivity('ATESTADO_EDICAO', `Atestado editado: ${form.title}`, 'INFO');
      } else {
        const res = await apiService.createTechnicalCertificate(payload);
        console.debug('Atestado created response:', res);
        try { (window as any).__lastAtestadoDebug.response = res; } catch (e) {}
        const companyName = companies.find(c => c.id === finalCompanyId)?.tradeName || 'Geral';
        logActivity('ATESTADO_CRIACAO', `Atestado adicionado: ${form.title} (${companyName})`, 'SUCCESS');
      }

      // reload documents and deduplicate by id to avoid visual duplicates
      try {
        const raw = await apiService.getTechnicalCertificates();
        const unique = Array.from(new Map(raw.map((d: any) => [d.id, d])).values());
        setDocs(unique);
      } catch (err) {
        console.warn('Falha ao recarregar atestados após salvar:', err);
      }

      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
    } catch (error:any) {
      console.error(error);
      await showAlert('Erro ao salvar atestado: ' + (error.message || error));
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    const ok = await showConfirm(`Excluir atestado "${title}"?`);
    if (!ok) return;
    try {
      await apiService.deleteTechnicalCertificate(id);
      setDocs(await apiService.getTechnicalCertificates());
      logActivity('ATESTADO_EXCLUSAO', `Atestado excluído: ${title}`, 'WARNING');
    } catch (error) {
      console.error(error);
      await showAlert('Erro ao excluir atestado.');
    }
  };

  const handleDownload = async (doc: OfficialDocument) => {
    try {
      if (doc.fileUrl) {
        const { downloadUrl } = await apiService.getPresignedDownloadUrl(doc.fileUrl);
        window.open(downloadUrl, '_blank');
        return;
      }

      if (doc.file && doc.file.base64) {
        const link = document.createElement('a');
        link.href = `data:${doc.file.type};base64,${doc.file.base64}`;
        link.download = doc.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      await showAlert('Nenhum arquivo anexado.');
    } catch (e) {
      console.error(e);
      await showAlert('Erro ao baixar arquivo.');
    }
  };

  const { showAlert, showConfirm } = useModal();

  const filteredDocs = docs.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) || doc.issuer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = companyFilter === 'ALL' || doc.companyId === companyFilter;
    const matchesStatus = statusFilter === 'ALL' || doc.status === statusFilter;
    return matchesSearch && matchesCompany && matchesStatus;
  });

  const getStatusBadge = (doc: OfficialDocument) => {
    const days = getDaysRemaining(doc.expirationDate);
    switch (doc.status) {
      case 'EXPIRED':
        return (
          <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/60 px-2 py-1 rounded-md text-xs font-bold animate-pulse">
            <AlertOctagon className="w-3.5 h-3.5" />
            VENCIDO há {Math.abs(days || 0)} dias
          </div>
        );
      case 'WARNING':
        return (
          <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md text-xs font-bold">
            <AlertTriangle className="w-3.5 h-3.5" />
            Vence em {days ?? 0} dias
          </div>
        );
      case 'VALID':
        return (
          <div className="flex items-center gap-1.5 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/60 px-2 py-1 rounded-md text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            Válido ({days ?? 0} dias)
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark px-2 py-1 rounded-md text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            Sem Validade
          </div>
        );
    }
  };

  return (
    <div className="space-y-6 pb-8 max-w-full overflow-x-hidden">
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Atestados de Capacidade Técnica
            </h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Gerencie atestados de capacidade técnica vinculados às empresas.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleOpenModal()} className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm">
              <Plus className="w-4 h-4" />
              Novo Atestado
            </button>
            <ClearFiltersButton onClear={clearFilters} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Briefcase className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
            </div>
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="w-full bg-blue-50 dark:bg-blue-900/30/50 border border-blue-100 text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer font-medium">
              <option value="ALL">Empresa: Todas</option>
              {companies.map(comp => (
                <option key={comp.id} value={comp.id}>{comp.tradeName}</option>
              ))}
            </select>
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
            </div>
            <input type="text" placeholder="Buscar por título ou emissor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none" />
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
            </div>
            <select value={'ATESTADO'} disabled className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-not-allowed">
              <option>Tipo: Atestado Técnico</option>
            </select>
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <AlertTriangle className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className={`w-full border text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer font-medium ${statusFilter === 'EXPIRED' ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/60 text-red-700 dark:text-red-300' : statusFilter === 'WARNING' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-surface-alt dark:bg-surface-dark-alt border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark'}`}>
              <option value="ALL">Status: Todos</option>
              <option value="VALID">✅ Válidos</option>
              <option value="WARNING">⚠️ Vencendo (15 dias)</option>
              <option value="EXPIRED">❌ Vencidos</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {filteredDocs.map((doc) => (
          <div key={doc.id} className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark hover:shadow-md transition-all group relative overflow-hidden flex flex-col">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${doc.status === 'EXPIRED' ? 'bg-red-500' : doc.status === 'WARNING' ? 'bg-amber-500' : doc.status === 'VALID' ? 'bg-green-500' : 'bg-gray-300'}`}></div>

            <div className="p-5 pl-6 flex-1">
              <div className="flex justify-between items-start mb-2">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-ink-placeholder dark:text-ink-placeholder-dark uppercase tracking-wider bg-surface-alt dark:bg-surface-dark-alt px-2 py-0.5 rounded border border-line dark:border-line-dark w-fit">{doc.type}</span>
                  {doc.category && (
                    <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 w-fit">
                      {doc.category}
                    </span>
                  )}
                  <div className="flex gap-1 mt-1">
                     {doc.hasCrea && <span className="text-[9px] font-bold text-white bg-blue-600 dark:bg-blue-500 px-1.5 py-0.5 rounded shadow-sm">CREA/CAT</span>}
                     {doc.hasArt && <span className="text-[9px] font-bold text-white bg-green-600 dark:bg-green-500 px-1.5 py-0.5 rounded shadow-sm">ART</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleOpenModal(doc)} className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-blue-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100" title="Editar"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(doc.id, doc.title)} className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-600 rounded bg-surface-alt dark:bg-surface-dark-alt hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark mb-1 leading-snug truncate" title={doc.title}>{doc.title}</h3>

              {/* Cliente emissor do atestado */}
              {doc.clientName && (
                <div className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded w-fit mb-2 border border-purple-100">
                  <Building2 className="w-3 h-3" />
                  <span className="font-medium truncate max-w-[200px]" title={doc.clientName}>Cliente: {doc.clientName}</span>
                </div>
              )}

              {/* Referência (Contrato/NF) */}
              {doc.reference && (
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded w-fit mb-2 border border-amber-100">
                  <FileText className="w-3 h-3" />
                  <span className="font-medium">Ref: {doc.reference}</span>
                </div>
              )}

                {/* Itens e quantidades do atestado */}
                {doc.items && doc.items.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-1">Itens:</div>
                    <ul className="text-sm text-ink-text dark:text-ink-text-dark list-disc list-inside">
                      {doc.items.map(it => (
                        <li key={it.id} className="truncate">{it.quantity} {it.unit || 'un'} — {it.description}</li>
                      ))}
                    </ul>
                  </div>
                )}

              <div className="flex items-center gap-1.5 text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-2">
                <Building2 className="w-3.5 h-3.5" />
                {doc.issuer || 'Emissor não informado'}
              </div>

              {/* Badge da empresa (nossa empresa executora) */}
              <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded w-fit mb-4 border border-blue-100">
                <Briefcase className="w-3 h-3" />
                <span className="font-medium truncate max-w-[200px]">{getCompanyName(doc.companyId)}</span>
              </div>

              {auditMap[doc.id] && auditMap[doc.id].length > 0 && (() => {
                const logs = auditMap[doc.id] || [];
                const createdLog = logs.find((l: any) => l.action === 'CREATE') || logs[0];
                const updatedLog = [...logs].reverse().find((l: any) => l.action === 'UPDATE');
                const deletedLog = logs.find((l: any) => l.action === 'DELETE');
                const actorCreated = getActorFromLog(createdLog);
                const actorUpdated = getActorFromLog(updatedLog);
                const actorDeleted = getActorFromLog(deletedLog);
                return (
                  <div className="mb-3 text-xs text-ink-placeholder dark:text-ink-placeholder-dark">
                    <div>Criado por {actorCreated?.name || '—'}{actorCreated?.email ? ` (${actorCreated.email})` : ''}{actorCreated?.timestamp ? ` em ${new Date(actorCreated.timestamp).toLocaleDateString()}` : ''}</div>
                    {actorUpdated && <div>Atualizado por {actorUpdated?.name || '—'}{actorUpdated?.email ? ` (${actorUpdated.email})` : ''}{actorUpdated?.timestamp ? ` em ${new Date(actorUpdated.timestamp).toLocaleDateString()}` : ''}</div>}
                    {actorDeleted && <div className="text-red-600 dark:text-red-300">Excluído por {actorDeleted?.name || '—'}{actorDeleted?.email ? ` (${actorDeleted.email})` : ''}{actorDeleted?.timestamp ? ` em ${new Date(actorDeleted.timestamp).toLocaleDateString()}` : ''}</div>}
                  </div>
                );
              })()}

              <div className="space-y-3 pt-3 border-t border-line dark:border-line-dark">
                <div className="flex justify-between items-center">{getStatusBadge(doc)}</div>

                <div className="flex justify-between text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase text-ink-placeholder dark:text-ink-placeholder-dark font-bold">Validade</span>
                    <div className="flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" />{doc.expirationDate && doc.expirationDate !== '' ? parseLocalDate(doc.expirationDate).toLocaleDateString('pt-BR') : 'Indefinida'}</div>
                  </div>
                  {doc.issueDate && doc.issueDate !== '' && (
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] uppercase text-ink-placeholder dark:text-ink-placeholder-dark font-bold">Emissão</span>
                      <span className="mt-0.5">{parseLocalDate(doc.issueDate).toLocaleDateString('pt-BR')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(doc.file || doc.fileUrl) && (
              <div className="px-5 py-3 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => handleDownload(doc)}>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-surface dark:bg-surface-dark rounded-lg border border-line dark:border-line-dark text-blue-600 dark:text-blue-300 shadow-sm"><FileText className="w-4 h-4" /></div>
                  <div className="flex-1 overflow-hidden">
                    <span className="text-xs font-medium text-ink-text dark:text-ink-text-dark truncate block w-full group-hover:text-blue-700">{doc.file ? doc.file.name : 'Documento Principal'}</span>
                    <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark block mt-0.5">{doc.file ? `${(doc.file.size / 1024).toFixed(1)} KB • ` : ''}Clique para abrir</span>
                  </div>
                  <Download className="w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark group-hover:text-blue-600" />
                </div>
              </div>
            )}

            {/* Seção de Anexos Complementares */}
            {doc.attachments && doc.attachments.length > 0 && (
              <div className="px-5 py-3 bg-gradient-to-r from-purple-50 to-blue-50 border-t border-line dark:border-line-dark hover:from-purple-100 hover:to-blue-100 transition-colors cursor-pointer" onClick={() => { setSelectedDocAttachments(doc); setAttachmentsModalOpen(true); }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-surface dark:bg-surface-dark rounded-lg border border-purple-200 text-purple-600 dark:text-purple-300 shadow-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-bold text-purple-700">
                      {doc.attachments.length} Anexo{doc.attachments.length > 1 ? 's' : ''} Complementar{doc.attachments.length > 1 ? 'es' : ''}
                    </span>
                    <span className="text-[10px] text-purple-500 block mt-0.5">ARTs, Certidões, CATs • Clique para visualizar</span>
                  </div>
                  <Eye className="w-4 h-4 text-purple-400 group-hover:text-purple-600 dark:text-purple-300" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredDocs.length === 0 && (
        <div className="text-center py-12 text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark border-dashed">
          <FileCheck2 className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark mx-auto mb-3" />
          <p>Nenhum atestado encontrado.</p>
          {companies.length === 0 && (<p className="text-sm text-red-500 dark:text-red-400 mt-2">Dica: Cadastre suas empresas no menu Configurações para organizar melhor.</p>)}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div key={`${form.issueDate}-${form.expirationDate}-${aiSuccess}`} className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt flex-shrink-0">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2"><Upload className="w-4 h-4 text-blue-600 dark:text-blue-300" />{editingDoc ? 'Editar Atestado' : 'Novo Atestado'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Empresa Vinculada *</label>
                <select className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark" value={form.companyId} onChange={e => setForm({...form, companyId: e.target.value})}>
                  <option value="" disabled>Selecione a empresa...</option>
                  {companies.map(comp => (<option key={comp.id} value={comp.id}>{comp.tradeName} ({comp.cnpj})</option>))}
                </select>
                {companies.length === 0 && (<p className="text-[10px] text-red-500 dark:text-red-400 mt-1">Nenhuma empresa cadastrada. Vá em Configurações.</p>)}
              </div>

              {!editingDoc && (
                <div onDrop={onDropFile} onDragOver={(e) => e.preventDefault()} className={`border-2 border-dashed rounded-xl transition-all ${isExtracting ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/30' : 'border-purple-300 bg-purple-50 dark:bg-purple-900/30/30 hover:bg-purple-50 dark:bg-purple-900/30'}`}>
                  {isExtracting ? (
                    <div className="flex flex-col items-center justify-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-200 border-t-purple-600 mb-3"></div><p className="text-sm font-bold text-purple-800 dark:text-purple-200">Extraindo Dados com IA...</p><p className="text-xs text-purple-600 dark:text-purple-300">Aguarde</p></div>
                  ) : aiSuccess ? (
                    <div className="flex flex-col items-center justify-center py-6"><CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300 mb-2" /><p className="text-sm font-bold text-green-800 dark:text-green-200">Dados Extraídos com Sucesso!</p><p className="text-xs text-green-600 dark:text-green-300">O formulário foi preenchido automaticamente.</p></div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-2"><div className="bg-surface dark:bg-surface-dark p-3 rounded-full shadow-sm mb-3"><Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-300" /></div><h4 className="font-bold text-purple-900 mb-1">Preenchimento Automático via IA</h4><p className="text-xs text-purple-700 max-w-xs mx-auto mb-3">Arraste o PDF do atestado aqui para que a IA preencha o cadastro e anexe o arquivo automaticamente.</p><label className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer shadow-sm">Selecionar PDF<input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => { if (e.target.files && e.target.files[0]) { setSelectedFile(e.target.files[0]); handleAiExtraction(e.target.files[0]); } }} /></label></div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Título *</label>
                <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: Atestado de Capacidade Técnica - Obras" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>

              {/* Seção: Dados do Cliente (Quem Emite o Atestado) */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  Dados do Cliente (Emissor do Atestado)
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Razão Social do Cliente *</label>
                    <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-yellow-50 dark:bg-yellow-900/30" placeholder="Principal identificador do atestado" value={form.clientName} onChange={e => setForm({...form, clientName: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">CNPJ do Cliente</label>
                      <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="XX.XXX.XXX/XXXX-XX" value={form.clientCNPJ} onChange={e => setForm({...form, clientCNPJ: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Referência (Contrato/NF)</label>
                      <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-yellow-50 dark:bg-yellow-900/30" placeholder="Nº do Contrato/NF" value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Descrição da Obra/Serviço */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3">Detalhes do Serviço e Classificação</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Categoria (Ex: Catracas, Software, Limpeza)</label>
                    <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Digite a categoria para agrupar este atestado" value={form.category || ''} onChange={e => setForm({...form, category: e.target.value})} />
                  </div>

                  <div className="flex gap-4 my-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500" checked={form.hasCrea || false} onChange={e => setForm({...form, hasCrea: e.target.checked})} />
                      <span className="text-sm text-ink-text dark:text-ink-text-dark font-medium">Possui CREA/CAT?</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-green-600 dark:text-green-300 rounded focus:ring-green-500" checked={form.hasArt || false} onChange={e => setForm({...form, hasArt: e.target.checked})} />
                      <span className="text-sm text-ink-text dark:text-ink-text-dark font-medium">Possui ART?</span>
                    </label>
                  </div>

                  {/* Itens quantitativos (ex: 03 Catracas, 01 Software) */}
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-2">Itens / Quantidades</label>
                    <div className="space-y-2">
                      {(form.items || []).map((it, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input placeholder="Descrição" className="flex-1 border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm" value={it.description || ''} onChange={e => {
                            const newItems = [...(form.items || [])];
                            newItems[idx] = { ...(newItems[idx] || {}), description: e.target.value } as any;
                            setForm({...form, items: newItems as any});
                          }} />
                          <input placeholder="Qtd" type="number" min="0" step="1" className="w-20 border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm" value={String(it.quantity ?? '')} onChange={e => {
                            const newItems = [...(form.items || [])];
                            newItems[idx] = { ...(newItems[idx] || {}), quantity: Number(e.target.value || 0) } as any;
                            setForm({...form, items: newItems as any});
                          }} />
                          <input placeholder="Un" className="w-20 border border-line-strong dark:border-line-dark-strong rounded px-2 py-1 text-sm" value={it.unit || ''} onChange={e => {
                            const newItems = [...(form.items || [])];
                            newItems[idx] = { ...(newItems[idx] || {}), unit: e.target.value } as any;
                            setForm({...form, items: newItems as any});
                          }} />
                          <button type="button" onClick={() => {
                            const newItems = (form.items || []).filter((_, i) => i !== idx);
                            setForm({...form, items: newItems as any});
                          }} className="text-sm text-red-600 dark:text-red-300 px-2">Remover</button>
                        </div>
                      ))}

                      <button type="button" onClick={() => {
                        const newItems = [...(form.items || []), { description: '', quantity: 1, unit: 'Un' }];
                        setForm({...form, items: newItems as any});
                      }} className="mt-1 inline-flex items-center gap-2 px-3 py-1 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded text-xs font-medium text-ink-text dark:text-ink-text-dark cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt shadow-sm">
                        <Plus className="w-3 h-3" /> Adicionar Item
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Descrição da Obra/Serviço</label>
                    <textarea className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none h-20 resize-none" placeholder="Descreva o serviço/obra executado..." value={form.workDescription} onChange={e => setForm({...form, workDescription: e.target.value})} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Local da Obra/Serviço</label>
                    <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Endereço ou local de execução" value={form.workLocation} onChange={e => setForm({...form, workLocation: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Período de Execução - Início</label>
                      <input type="date" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" value={form.executionPeriodStart || ''} onChange={e => setForm({...form, executionPeriodStart: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Período de Execução - Fim</label>
                      <input type="date" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" value={form.executionPeriodEnd || ''} onChange={e => setForm({...form, executionPeriodEnd: e.target.value})} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção: Dados Adicionais */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3">Dados Adicionais</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Órgão Emissor</label>
                    <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" placeholder="Ex: Empresa X" value={form.issuer} onChange={e => setForm({...form, issuer: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Tipo</label>
                    <select className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark" value={'ATESTADO'} disabled>
                      <option value="ATESTADO">Atestado Técnico</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Seção: Datas e Validade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data de Emissão</label>
                  <input type="date" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" value={form.issueDate || ''} onChange={e => setForm({...form, issueDate: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data de Validade</label>
                  <input type="date" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none" value={form.expirationDate || ''} onChange={e => setForm({...form, expirationDate: e.target.value})} />
                  <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">Deixe em branco se não expirar.</p>
                </div>
              </div>

              {/* Seção: Assinatura do Cliente */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3">Dados do Signatário (Cliente)</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Nome do Signatário</label>
                    <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-blue-50 dark:bg-blue-900/30" placeholder="Quem assina pelo cliente" value={form.signatoryName} onChange={e => setForm({...form, signatoryName: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Cargo/Função</label>
                      <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-blue-50 dark:bg-blue-900/30" placeholder="Ex: Diretor" value={form.signatoryRole} onChange={e => setForm({...form, signatoryRole: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">CPF do Signatário</label>
                      <input type="text" className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-blue-50 dark:bg-blue-900/30" placeholder="XXX.XXX.XXX-XX" value={form.signatoryCPF} onChange={e => setForm({...form, signatoryCPF: e.target.value})} />
                    </div>
                  </div>
                </div>
              </div>

                  <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Arquivo Principal (PDF/Imagem) *</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="block w-full text-sm text-ink-subtitle dark:text-ink-subtitle-dark file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all" />
                {editingDoc?.file && !selectedFile && (<p className="text-[10px] text-green-600 dark:text-green-300 mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Arquivo atual: {editingDoc.file.name}</p>)}
              </div>

              {/* Upload de Múltiplos Arquivos (ART, CAT, etc) */}
               <div className="border-t pt-4">
                   <h3 className="text-sm font-semibold text-ink-text dark:text-ink-text-dark mb-3">Anexos Complementares (ART, CAT, etc)</h3>
                   <div className="border-2 border-dashed border-line-strong dark:border-line-dark-strong rounded-lg p-4 text-center hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors">
                      <Upload className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark mx-auto mb-2" />
                      <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Adicione ARTs, Certidões ou Documentos Auxiliares</p>
                      <input type="file" multiple className="hidden" id="multi-upload" onChange={async (e) => {
                          const files = e.target.files ? Array.from(e.target.files) as File[] : [];
                          if (files.length === 0) return;

                          // companyId definitivo (fallback)
                          const finalCompanyId = form.companyId || (companies.length > 0 ? companies[0].id : undefined);
                          if (!finalCompanyId) {
                            alert('Selecione a empresa antes de anexar arquivos.');
                            return;
                          }

                          const newAtts: any[] = [];
                          for (const f of files) {
                            try {
                              const ext = (f.name.split('.').pop() || 'pdf').toLowerCase();
                              const key = `attachments/${finalCompanyId}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${ext}`;
                              const presign = await apiService.getPresignedUploadUrl(key, f.type || 'application/pdf');
                              await apiService.uploadFileToS3(presign.uploadUrl, f);
                              newAtts.push({ name: f.name, url: key, type: f.type, size: f.size });
                            } catch (err) {
                              console.warn('Falha ao enviar arquivo para S3:', f.name, err);
                            }
                          }

                          setForm(prev => ({ ...(prev || {}), attachments: [ ...(prev.attachments || []), ...newAtts ] }));
                      }} />
                      <label htmlFor="multi-upload" className="mt-2 inline-block px-3 py-1 bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded text-xs font-medium text-ink-text dark:text-ink-text-dark cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt shadow-sm">
                        Selecionar Arquivos
                      </label>
                   </div>

           {/* Lista de Anexos Pendentes (Novos, já enviados ao S3 mas ainda não persistidos no DB) */}
           {(form.attachments || []).filter((a: any) => !a.id).length > 0 && (
             <div className="mt-3 space-y-2">
               <p className="text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark">Arquivos para enviar:</p>
               {(form.attachments || []).filter((a: any) => !a.id).map((f: any, idx: number) => (
                 <div key={f.url || idx} className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded text-sm text-blue-700 dark:text-blue-300">
                   <span className="truncate">{f.name} {(f.size ? `(${(f.size / 1024).toFixed(0)}KB)` : '')}</span>
                   <button onClick={() => {
                     const newAtts = (form.attachments || []).filter((att:any) => att.url !== f.url);
                     setForm(prev => ({ ...(prev || {}), attachments: newAtts }));
                   }} className="text-red-500 dark:text-red-400 hover:text-red-700">
                     <Trash2 className="w-4 h-4" />
                   </button>
                 </div>
               ))}
             </div>
           )}

                   {/* Lista de Anexos Existentes (Edição) */}
           {(form.attachments || []).filter((a:any) => a.id).length > 0 && (
             <div className="mt-3 space-y-2">
               <p className="text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark">Anexos já salvos:</p>
               {(form.attachments || []).filter((a:any) => a.id).map((att:any, idx:number) => (
                 <div key={att.id || idx} className="flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt px-3 py-2 rounded text-sm text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark">
                   <div className="flex items-center gap-2 overflow-hidden">
                     <FileText className="w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark flex-shrink-0" />
                     <span className="truncate" title={att.name}>{att.name}</span>
                   </div>
                   <div className="flex items-center gap-2">
                     {att.url && (
                     <button onClick={async () => {
                       try {
                         const { downloadUrl } = await apiService.getPresignedDownloadUrl(att.url);
                         window.open(downloadUrl, '_blank');
                       } catch(e) { alert('Erro ao abrir arquivo'); }
                     }} className="text-blue-600 dark:text-blue-300 hover:text-blue-800 text-xs underline mr-2">
                       Abrir
                     </button>
                     )}
                     <button onClick={() => {
                       // Se o anexo já possui id (salvo), marcar para remoção; caso contrário remover da lista
                       if (att.id) {
                         setDeleteAttachmentIds(prev => ([...prev, att.id]));
                         setForm(prev => ({ ...(prev || {}), attachments: (prev.attachments || []).filter((a:any) => a.id !== att.id) }));
                       } else {
                         setForm(prev => ({ ...(prev || {}), attachments: (prev.attachments || []).filter((a:any) => a.url !== att.url) }));
                       }
                     }} className="text-red-500 dark:text-red-400 hover:text-red-700">
                       <Trash2 className="w-4 h-4" />
                     </button>
                   </div>
                 </div>
               ))}
             </div>
           )}
               </div>
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3 flex-shrink-0">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">Cancelar</button>
              <button onClick={handleSave} disabled={isSaving} className={`px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg flex items-center gap-2 shadow-sm ${isSaving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-brand-600'}`}>
                <CheckCircle className="w-4 h-4" />{editingDoc ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Anexos */}
      {attachmentsModalOpen && selectedDocAttachments && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setAttachmentsModalOpen(false)}>
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-gradient-to-r from-purple-50 to-blue-50">
              <h3 className="font-bold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Anexos Complementares
              </h3>
              <button onClick={() => setAttachmentsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-1 bg-surface-alt dark:bg-surface-dark-alt">
              <div className="bg-surface dark:bg-surface-dark rounded-lg p-3 mb-2">
                <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mb-1">
                  <span className="font-semibold text-ink-title dark:text-ink-title-dark">{selectedDocAttachments.title}</span>
                </p>
                <p className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark">
                  {getCompanyName(selectedDocAttachments.companyId)}
                </p>
              </div>
            </div>

            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              <p className="text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark mb-3">Selecione o anexo que deseja visualizar:</p>

              {selectedDocAttachments.attachments && selectedDocAttachments.attachments.map((att: any, idx: number) => (
                <div key={att.id || idx} className="group bg-gradient-to-r from-gray-50 to-blue-50 hover:from-blue-50 hover:to-purple-50 border border-line dark:border-line-dark rounded-lg p-3 cursor-pointer transition-all hover:shadow-md" onClick={async () => {
                  try {
                    const { downloadUrl } = await apiService.getPresignedDownloadUrl(att.url);
                    window.open(downloadUrl, '_blank');
                  } catch(e) {
                    alert('Erro ao abrir arquivo');
                  }
                }}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-surface dark:bg-surface-dark rounded-lg border border-purple-200 text-purple-600 dark:text-purple-300 shadow-sm group-hover:border-purple-400 transition-colors">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink-title dark:text-ink-title-dark truncate group-hover:text-purple-700 transition-colors">
                        {att.name}
                      </p>
                      <p className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark mt-0.5">
                        {att.size ? `${(att.size / 1024).toFixed(1)} KB • ` : ''}
                        {att.uploadedAt ? new Date(att.uploadedAt).toLocaleDateString('pt-BR') : 'Data não disponível'}
                      </p>
                    </div>
                    <Eye className="w-5 h-5 text-ink-placeholder dark:text-ink-placeholder-dark group-hover:text-purple-600 dark:text-purple-300 transition-colors" />
                  </div>
                </div>
              ))}

              {(!selectedDocAttachments.attachments || selectedDocAttachments.attachments.length === 0) && (
                <div className="text-center py-8 text-ink-placeholder dark:text-ink-placeholder-dark">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum anexo disponível</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end">
              <button onClick={() => setAttachmentsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateList;
